import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UtilsService } from '../../../shared/utils.service';
import { SiteModel } from '../../../models/site.model';
import { AppService } from '../../../app.service';
import { ApiRequestService } from '../../../shared/api-request.service';
import { RBACService } from '../../../shared/rbac.service';

@Component({
  selector: 'app-sites-details-layout',
  templateUrl: './sites-details-layout.component.html',
  styleUrls: ['./sites-details-layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SitesDetailsLayoutComponent implements OnInit {
  // the site model placeholder
  site: SiteModel = new SiteModel();

  isViewMode: boolean = false;

  // RBAC permission flags. Resolved once in ngOnInit() so the template does not
  // re-evaluate permission checks on every change detection cycle.
  canEdit: boolean = false;
  canDelete: boolean = false;

  constructor(
    public router: Router,
    public route: ActivatedRoute,
    public app: AppService,
    private api: ApiRequestService,
    public utils: UtilsService,
    public rbac: RBACService
  ) {}

  ngOnInit(): void {
    // Resolve the RBAC permission flags used by the template before any early return. These mirror
    // the API guards in SitesController: update needs sites:view + sites:edit and destroy needs
    // sites:view + sites:delete. sites:view cannot be assumed here because this layout is also
    // reachable with only sites:create (the new site form), per the access check below.
    this.canEdit = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES,
          RBACService.RBAC_A.A_VIEW
        ),
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES,
          RBACService.RBAC_A.A_EDIT
        )
      ],
      true
    );
    this.canDelete = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES,
          RBACService.RBAC_A.A_VIEW
        ),
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES,
          RBACService.RBAC_A.A_DELETE
        )
      ],
      true
    );

    if (
      !this.rbac.userCanAccess([
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES,
          RBACService.RBAC_A.A_CREATE
        ),
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES,
          RBACService.RBAC_A.A_VIEW
        ),
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES,
          RBACService.RBAC_A.A_EDIT
        )
      ])
    ) {
      this.rbac.showGenericAccessDeniedMessage();
      this.router.navigate(['/sites']);
      return;
    }

    // Check if we are in view mode. Extract it from the second last segment of the url.
    const action: string = this.router.url
      .split('?')[0]
      .split('/')
      .slice(-2, -1)[0];

    this.isViewMode = action === 'view';

    // Get the site id from the route params.
    const parent_id: number = Number(
      this.route.parent.snapshot.params['parent_id']
    );
    const child_id: number = Number(
      this.route.parent.snapshot.params['child_id']
    );

    // Check if we are updating a site or creating a new one.
    if (
      child_id ||
      (typeof this.route.parent.snapshot.params['child_id'] == 'undefined' &&
        parent_id)
    ) {
      // Store the site id.
      this.site.id = child_id ? child_id : parent_id;
      // If this is a child site, store the parent site id.
      if (child_id && parent_id) {
        this.site.parent_site_id = parent_id;
      }
    } else {
      // Get and store the parent site id if it is present.
      if (parent_id) {
        // Store the site id.
        this.site.parent_site_id = parent_id;
      }
    }
  }

  /**
   * Remove a single site.
   */
  onRemove(): void {
    // Mirrors SitesController@destroy, which requires sites:view + sites:delete.
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES,
            RBACService.RBAC_A.A_DELETE
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage();
      return;
    }

    this.utils.showModal(
      'Remove ' +
        this.utils.getLangTerm(
          this.site.parent_site_id ? 'child-sites.singular' : 'sites.singular',
          this.site.parent_site_id ? 'Subsite' : 'Site'
        ),
      'Are you sure you want to remove this ' +
        this.utils
          .getLangTerm(
            this.site.parent_site_id
              ? 'child-sites.singular'
              : 'sites.singular',
            this.site.parent_site_id ? 'Subsite' : 'Site'
          )
          .toLowerCase() +
        '?',
      (): void => {
        this.api
          .makeRequest('delete', 'v1/sites/' + this.site.id)
          .then(
            (response: any): void => {
              this.utils.showToast(
                'The ' +
                  this.utils
                    .getLangTerm(
                      this.site.parent_site_id
                        ? 'child-sites.singular'
                        : 'sites.singular',
                      this.site.parent_site_id ? 'Subsite' : 'Site'
                    )
                    .toLowerCase() +
                  ' was removed.'
              );
              this.router.navigate(['/sites']);
            }
          )
          .catch(
            (err: any): void => {
              this.utils.showModal('Error', err.error.message);
            }
          );
      }
    );
  }

  protected readonly RBACService = RBACService;
}
